import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';

import { aiApi } from 'apis/ai';
import { AppDispatch, AppState } from 'bootstrap.store';
import * as conversationActions from 'components/conversation/actions/conversation';
import { useGetComposeModalCopilotActions } from 'components/editorCopilotDropdown/utils/composeModalCopilotActions';
import { notificationActions } from 'components/notifications/actions/notification';
import * as personalActions from 'components/settings/actions/personal';
import { templatesActions } from 'components/settings/actions/templates';
import {
  AddUserPayload,
  GetUserPayload,
  userModalActions,
} from 'components/usersModal';
import { copilotUtils } from 'utils/copilot';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';
import { ServerStorageProps, withServerStorage } from 'utils/useServerStorage';

import { hasFeature } from '../../featureFlag';
import { composeEmailActions } from '../actions/composeEmailActions';
import {
  EmailPayload,
  SaveDraftPayload,
  SendEmailResponseT,
} from '../api/composeEmail';
import { LocalStorageEmailDrafts } from '../helper/DraftEmailHelper';
import { selectInitialValues } from '../selectors/ComposeEmail';

import ComposeEmailModalWithReduxForm, {
  ComposeEmailModalFormValues,
  FORM_NAME,
} from './ComposeEmailModal';

/** The redux-form error type. */
export type ComposeEmailModalErrorType = { _error: string };

const mapStateToProps = (
  state: AppState,
  props: ServerStorageProps<'organization', 'organizationStorage'>,
) => {
  const metaConversation = state.composeEmail.parentConversation;
  const rewrittenHtmlEmailMessages = state.emailMessages.messages.filter(
    (message) => message.csid === metaConversation?.conversation?.id,
  );

  return {
    meta: state.composeEmail.meta,
    ui: state.composeEmail.ui,
    organization: state.settings.organization.organization,
    users: state.composeEmail.users,
    profile: state.settings.profile.profile,
    metaConversation,
    rewrittenHtmlEmailMessages,
    fromEmails: state.composeEmail.fromEmails,
    initialValues: selectInitialValues(state, props.organizationStorage.data),
    formValues: getFormValues(FORM_NAME)(state) as ComposeEmailModalFormValues,
    hasFFWriteHTMLEmail: hasFeature(state, 'write-html-email'),
  };
};

const mapDispatchToProps = (dispatch: AppDispatch) => {
  return {
    composeEmailFindUser: bindActionToPromise(
      dispatch,
      composeEmailActions.composeEmailFindUser,
    ),
    composeEmailGetAllFrom: bindActionToPromise(
      dispatch,
      composeEmailActions.composeEmailGetAllFrom,
    ),
    composeEmailSaveDraft: bindActionToPromise<
      SaveDraftPayload,
      LocalStorageEmailDrafts,
      void
    >(dispatch, composeEmailActions.composeEmailSaveDraft),
    composeEmailRemoveDraft: bindActionToPromise(
      dispatch,
      composeEmailActions.composeEmailRemoveDraft,
    ),
    composeEmailCloseModal: () =>
      dispatch(composeEmailActions.composeEmailCloseModal()),
    conversationLinkChild: bindActionToPromise(
      dispatch,
      conversationActions.actions.conversationLinkChild,
    ),
    userModalAddUser: (payload: AddUserPayload) =>
      dispatch(userModalActions.user.add(payload)),
    userModalGetUser: (payload: GetUserPayload) =>
      dispatch(userModalActions.user.get(payload)),
    validateEmail: (email: string) =>
      dispatch(userModalActions.validation.email(email)),

    sendEmail: bindActionToPromise<EmailPayload, SendEmailResponseT, {}>(
      dispatch,
      composeEmailActions.composeEmailSend,
    ),
    templatesRender: bindActionToPromise(
      dispatch,
      templatesActions.templatesRender,
    ),
    notificationAdd: (payload: any) =>
      dispatch(notificationActions.notificationAdd(payload)),
    notificationRemoveById: (payload: any) =>
      dispatch(notificationActions.notificationRemoveById(payload)),
    updateLastUsedEmailIntegration: bindActionToPromise(
      dispatch,
      personalActions.actions.personalLastFromEmailIntegration,
    ),
  };
};

function withGetCopilotActionsHook(Component) {
  return function WrappedComponent(props) {
    const [messageEditorText, setMessageEditorText] = useState('');

    const [readAIWritingStyleGuideMatch, { data: styleGuide }] =
      aiApi.useLazyReadAIWritingStyleGuideMatchQuery();

    useEffect(() => {
      if (props.ui.isModalOpen) {
        readAIWritingStyleGuideMatch({
          features: 'impr_reply',
          channels: 'email',
        });
      }
    }, [props.ui.isModalOpen, readAIWritingStyleGuideMatch]);

    const onRetrievalOfEditorText = (receivedEditorText: string) => {
      setMessageEditorText(receivedEditorText);
    };

    const { editorCopilotActions, internalNoteEditorCopilotActions } =
      useGetComposeModalCopilotActions(
        'email',
        useCallback(() => {
          const hasStyleGuide = !!styleGuide?.data?.match;
          const { isDisabled, tooltipContent } =
            copilotUtils.matchToStyleGuide.getStatus({
              text: messageEditorText,
              channel: 'email',
              hasStyleGuideForChannel: hasStyleGuide,
            });

          if (isDisabled) {
            return tooltipContent;
          }

          return false;
        }, [styleGuide, messageEditorText]),
      );

    return (
      <Component
        {...props}
        editorCopilotActions={editorCopilotActions}
        internalNoteEditorCopilotActions={internalNoteEditorCopilotActions}
        onRetrievalOfEditorText={onRetrievalOfEditorText}
      />
    );
  };
}

export type StateProps = ReturnType<typeof mapStateToProps>;
export type DispatchProps = ReturnType<typeof mapDispatchToProps>;

export default withServerStorage({
  scope: 'organization',
  propName: 'organizationStorage',
})(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(withGetCopilotActionsHook(ComposeEmailModalWithReduxForm)),
);
